import {COLOR_NG, COLOR_OK} from '_logics/LFCConst';
import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {Chip, Grid, Stack, Switch, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';

import MapIconViewer, {formatIconId, MapIconProps} from './MapIconViewer';

const SELECT_COLOR = 'rgb(245, 124, 0)';

export type SelectedMapIconProps = {
  imageNo: number;
  map: string;
};

type ImageResultsProps = {
  imgWidth: number;
  imageRootPath: string;
  workBases: any[]; //ワーク画像
  mapPointResults: any[]; //Map位置&検査結果情報
  selectedJobTypes: number[]; //選択中のジョブ判定
  checkResultsJob: any[]; //部位×ジョブ判定（job_type数値を含む）
  selected: SelectedMapIconProps | null;
  onSelected: (imageNo: number, map: string) => void;
};

const ImageResults = (props: ImageResultsProps) => {
  const history = useHistory();
  const theme = useTheme();
  const [workBases, setWorkBases] = useState<any[]>(props.workBases);
  const [mapPointResults, setMapPoints] = useState<any[]>(props.mapPointResults);
  const [ts, setTs] = useState(new Date().getTime()); //ワーク画像のキャッシュ防止用
  const [checkedShowNgOnly, setCheckedShowNgOnly] = useState(false); //NGのみ表示フラグ
  const [checkedShowMap, setCheckedShowMap] = useState(true); //部位名表示フラグ
  const [innerSelectedIconId, setInnerSelectedIconId] = useState<string | null>(null); //(image_no)-(map)

  /***
   * NGのみ表示チェックボックス変更
   */
  const onChangeShowNgOnly = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setCheckedShowNgOnly(checked);
  };

  /***
   * 部位名表示チェックボックス変更
   */
  const onChangeShowMap = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setCheckedShowMap(checked);
  };

  /***
   * 部位クリックイベント
   */
  const onMapClick = (iconId: string, imageNo: number, map: string) => {
    setInnerSelectedIconId(iconId);
    props.onSelected(imageNo, map);
  };

  useEffect(() => {
    setWorkBases(props.workBases);
    setTs(new Date().getTime());
  }, [props.workBases]);

  useEffect(() => {
    setMapPoints(props.mapPointResults);
  }, [props.mapPointResults]);

  /***
   * 部位クリックイベント
   */
  useEffect(() => {
    if (props.selected === null) {
      return;
    }
    const iconid = formatIconId(props.selected.imageNo, props.selected.map);
    if (iconid === innerSelectedIconId) {
      return;
    }
    setInnerSelectedIconId(iconid);
    props.onSelected(props.selected.imageNo, props.selected.map);
  }, [props.selected]);

  const jobSelected = props.selectedJobTypes.length > 0;

  /***
   * 選択中ジョブがその部位で使用されているか（いずれか一致）
   * ジョブ未選択時は全て対象
   */
  const isMapVisibleForJobs = (map: string): boolean => {
    if (!jobSelected) return true;
    return props.checkResultsJob.some(
      (j: any) => j['map'] === map && props.selectedJobTypes.includes(j['job_type'])
    );
  };

  /***
   * 部位の判定を解決する
   * ジョブ選択時は選択ジョブ内に1件でもNGがあればNG(1)、無ければOK(0)
   * ジョブ未選択時は元の判定値をそのまま返す
   */
  const resolveJudge = (map: string, originalJudge: any): any => {
    if (!jobSelected) return originalJudge;
    const targets = props.checkResultsJob.filter(
      (j: any) => j['map'] === map && props.selectedJobTypes.includes(j['job_type'])
    );
    return targets.some((j: any) => j['judge'] !== 0) ? 1 : 0;
  };

  return mapPointResults.length === 0 ? null : (
    <Grid container>
      {workBases.length > 0 && (
        <Grid item xs={12}>
          <Stack direction="row" flexWrap={'wrap'}>
            <Stack direction="row" alignItems="center" mr={1} mb={1}>
              <Chip size="small" label="NGアイコンのみ" variant="outlined" />
              <Switch checked={checkedShowNgOnly} onChange={onChangeShowNgOnly} />
            </Stack>
            <Stack direction="row" alignItems="center" mr={1} mb={1}>
              <Chip size="small" label="部位名" variant="outlined" />
              <Switch checked={checkedShowMap} onChange={onChangeShowMap} />
            </Stack>
          </Stack>
        </Grid>
      )}
      {/* ワーク画像登録されているデータの表示 */}
      {workBases.map((mapBase: any, index: number) => {
        const image_no = mapBase['image_no'];

        //該当する画像Noのデータ分だけで処理
        const resIcons = mapPointResults
          .filter((cr: any) => cr['image_no'] === image_no)
          //ジョブ選択時は選択ジョブが使用されている部位（いずれか一致）のみ
          .filter((cr: any) => isMapVisibleForJobs(cr['map']))
          .map((r: any) => {
            //ジョブ選択時は選択ジョブ内に1件でもNGがあれば赤
            const judge = resolveJudge(r['map'], r['judge']);
            return {
              id: `${formatIconId(image_no, r['map'])}`,
              map: r['map'],
              color:
                judge === 0
                  ? checkedShowNgOnly
                    ? ''
                    : 'rgba(0, 255, 0, 1)'
                  : 'rgba(255, 0, 0, 1)',
              position: {relativeX: r['relative_x'], relativeY: r['relative_y']},
              selected: innerSelectedIconId === formatIconId(image_no, r['map']),
              isShowLabel: checkedShowNgOnly && judge === 0 ? false : checkedShowMap,
              isAttention: judge !== 0
            } as MapIconProps;
          })
          .filter((icon: MapIconProps | null): icon is MapIconProps => icon !== null);

        return (
          <Grid item xs={12} sx={{mb: 1}} key={`work-image-grid-${image_no}-${index}`}>
            <MapIconViewer
              containerWidth={props.imgWidth}
              containerHeight={props.imgWidth * mapBase['aspect_ratio']}
              imgPath={`${props.imageRootPath}${mapBase['image_path']}?${ts}`}
              icons={resIcons}
              iconRatio={mapBase['icon_ratio']}
              onIconClick={onMapClick}
              showBorder={false} // 登録済み画像なので枠線を表示しない
            ></MapIconViewer>
          </Grid>
        );
      })}
      {/* ワーク画像登録されてないデータの表示 */}
      {workBases.length === 0 &&
        [99].map((image_no: number, index: number) => {
          //画像Noだけで絞った結果
          const fltMapPoints = mapPointResults
            .filter((cr: any) => cr['image_no'] === image_no)
            //ジョブ選択時は選択ジョブが使用されている部位（いずれか一致）のみ
            .filter((cr: any) => isMapVisibleForJobs(cr['map']))
            .map((cr: any) => {
              //ジョブ選択時は選択ジョブ内に1件でもNGがあればNG
              if (!jobSelected) return cr;
              return {...cr, judge: resolveJudge(cr['map'], cr['judge'])};
            });

          return (
            <Grid item xs={12} sx={{mb: 1}} key={`work-text-grid-${image_no}-${index}`}>
              <Stack
                sx={{
                  border: '3px solid gray', // 画像登録パターンと同じスタイル
                  padding: 2,
                  position: 'relative'
                }}
              >
                {/* TODO: */}
                {/* <Alert
                key={'alert_warn'}
                variant="outlined"
                severity="warning"
                sx={{mb: 2}}
              >{`この機種はワーク画像または部位の位置が登録されていません`}</Alert> */}
                <Stack direction="row" flexWrap={'wrap'}>
                  {fltMapPoints.map((r: any, index: number) => {
                    return (
                      <Stack
                        direction="row"
                        alignItems="center"
                        key={`stack-${r['map']}-${index}`}
                        mr={1}
                        mb={1}
                      >
                        <Chip size="small" label="部位判定" />
                        <Stack
                          direction="row"
                          onClick={() => {
                            onMapClick(formatIconId(99, r['map']), 99, r['map']);
                          }}
                          sx={{
                            border:
                              innerSelectedIconId === formatIconId(image_no, r['map'])
                                ? `3px solid ${SELECT_COLOR}`
                                : '',
                            cursor: 'pointer'
                          }}
                        >
                          <Typography ml={1} variant="h6" noWrap>
                            {r['map'] || '-'}
                          </Typography>
                          <Typography
                            ml={1}
                            px={2}
                            variant="h6"
                            noWrap
                            sx={{
                              backgroundColor: r['judge'] === 0 ? COLOR_OK : COLOR_NG,
                              animation:
                                r['judge'] === 0
                                  ? ''
                                  : 'map-pulse 0.75s infinite, map-flash 0.75s infinite'
                            }}
                          >
                            {`${r['judge'] === 0 ? 'OK' : 'NG'}`}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>

                {/* 画像未登録の通知とリンク */}
                <Typography
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    color: theme.palette.primary.dark, // メインカラー暗め
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                  title="画像登録する場合はここをクリック"
                  onClick={() => {
                    // ワーク画像登録メニューへの遷移
                    history.push('/common/work-images');
                  }}
                >
                  ※画像未登録機種
                </Typography>
              </Stack>
            </Grid>
          );
        })}
    </Grid>
  );
};

export default ImageResults;
